import { useAppToast } from "@/hooks/useAppToast";
import { useWhatsAppSentNote } from "@/hooks/useWhatsApp";
import {
  useFoodPaymentVerifyMutation,
  useGenerateFoodTicketMutation,
} from "@/store/services/foodbooking";
import {
  Box,
  Button,
  Flex,
  FlexProps,
  Icon,
  Stack,
  Text,
} from "@chakra-ui/react";
import { PaymentStatusCard, PaymentStatusCardProps } from "@picovico/ui";
import { PAYMENT_ERROR } from "@picovico/ui/constant/alertMessage";
import {
  ServerError,
  catchErrorHandler,
  downloadFile,
  getLocalStorageData,
  parseServerError,
  removeLocalStorageData,
} from "@picovico/utils";
import { TransactionStatusMessage } from "constants/message";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { FiInfo } from "react-icons/fi";
import { useAppSelector } from "store";
import {
  useGenerateTicketMutation,
  useGetPaymentStatusMutation,
} from "store/services/booking";
import PaymentError from "../Error/payment-error";
import { FullPageLoader } from "../Loader";
import { WhatsAppSentNote } from "../WhatsApp";

const DEFAULT_COMPLETION_MESSAGE =
  "Incase you cannot download the ticket, it can be downloaded from your profile section. You can also use the QR code(from profile section) at check-in instead of a physical ticket.";

/** Keys of an error object that hold no sentence for people. */
const NOT_A_SENTENCE = new Set(["code", "status_code"]);

/**
 * Every text in a parsed server error: a string, a list, or a field map
 * (its `message` and `detail` first, codes left out).
 */
function errorTexts(value: unknown): string[] {
  if (typeof value === "string") return value.trim() ? [value] : [];
  if (Array.isArray(value)) return value.flatMap(errorTexts);
  if (value && typeof value === "object") {
    const entries = Object.entries(value).filter(
      ([key]) => !NOT_A_SENTENCE.has(key),
    );
    const first = (key: string) => key === "message" || key === "detail";
    return [
      ...entries.filter(([key]) => first(key)),
      ...entries.filter(([key]) => !first(key)),
    ].flatMap(([, entry]) => errorTexts(entry));
  }
  return [];
}

/**
 * The verify call's refusal as one sentence for `PaymentError`. A field error
 * (`{transaction_code: […]}`, which a second verify of the same payment gets,
 * e.g. on a reload of the callback page) is an object, and React cannot
 * render one: it crashed the page with "Objects are not valid as a React
 * child". No HTTP answer at all keeps the generic payment message.
 */
export function paymentErrorText(error: unknown): string {
  if (!error || typeof error !== "object") return PAYMENT_ERROR;
  const parsed = parseServerError(error as ServerError, PAYMENT_ERROR);
  return errorTexts(parsed)[0] ?? PAYMENT_ERROR;
}

type BookingPaymentStatusCardProps = Omit<
  PaymentStatusCardProps,
  "status" | "messages" | "actionButton"
> &
  FlexProps & {
    supportEmail?: string;
    downloadButtonProps?: React.ComponentProps<typeof Button>;
    completionMessage?: string;
    completionMessageBoxProps?: React.ComponentProps<typeof Box>;
    /** Styling for "Your ticket is on its way to WhatsApp (…)" (whatsapp-messaging 04 §3). */
    whatsappNoteProps?: FlexProps;
  };

export function BookingPaymentStatusCard({
  supportEmail,
  downloadButtonProps,
  completionMessage,
  completionMessageBoxProps,
  whatsappNoteProps,
  ...props
}: BookingPaymentStatusCardProps) {
  const company_idx =
    useAppSelector((state) => state.user.user.cinema_company.idx) ||
    getLocalStorageData("companyIdx");
  const cinema_hall = getLocalStorageData("cinema_hall");

  const [getPaymentStatus, { isLoading, data, error }] =
    useGetPaymentStatusMutation({});
  const [
    getFoodPaymentStatus,
    {
      isLoading: isFoodPaymentLoading,
      data: foodPaymentData,
      error: foodPaymentError,
    },
  ] = useFoodPaymentVerifyMutation({});
  const router = useRouter();

  const isFoodPage = router.pathname.includes("/payment/food");
  // The booking this tab paid for, read before the effect below clears it once the status is in.
  const [paidBookingNo] = useState<string | null>(
    () => getLocalStorageData("booking_no") || null,
  );

  const paymentData = isFoodPage ? foodPaymentData?.data : data?.data;
  const { success, error: errorToast } = useAppToast();
  const pidx = (router.query?.pidx as string) || "";
  const [generateTicket, { isLoading: isGeneratingTicket }] =
    useGenerateTicketMutation({});
  const [generateFoodTicket, { isLoading: isGeneratingFoodTicket }] =
    useGenerateFoodTicketMutation({});

  useEffect(() => {
    if (pidx) {
      if (isFoodPage) {
        getFoodPaymentStatus({ pidx, cinema_hall });
      } else {
        getPaymentStatus({
          company_idx,
          pidx,
        });
      }
    }
  }, [
    pidx,
    getPaymentStatus,
    company_idx,
    isFoodPage,
    getFoodPaymentStatus,
    cinema_hall,
  ]);

  useEffect(() => {
    if (paymentData?.transaction_code && paymentData?.auto_download) {
      const response = isFoodPage
        ? generateFoodTicket({
            idx: paymentData?.idx,
            cinema_hall,
          })
        : generateTicket({
            transaction_code: paymentData?.transaction_code,
          });

      response
        .unwrap()
        .then((res: any) => {
          if ("data" in res && res.data instanceof Blob)
            downloadFile(res.data, `ticket.pdf`);
          success("Ticket Downloaded Successfully");
        })
        .catch((err) => {
          catchErrorHandler(err, errorToast);
        });
    }
  }, [
    generateTicket,
    paymentData?.transaction_code,
    errorToast,
    paymentData?.auto_download,
    success,
    cinema_hall,
    paymentData?.idx,
    isFoodPage,
    generateFoodTicket,
  ]);

  useEffect(() => {
    if (paymentData?.transaction_status) {
      removeLocalStorageData("booking_no");
    }
  }, [paymentData?.transaction_status]);

  // What the checkout asked to send on WhatsApp, kept in this tab across the gateway round trip.
  const whatsappKind = isFoodPage ? "food" : "ticket";
  const whatsappNumber = useWhatsAppSentNote(
    whatsappKind,
    isFoodPage
      ? [pidx, paymentData?.pidx]
      : [pidx, paymentData?.pidx, paidBookingNo],
    paymentData?.transaction_status || paymentData?.status,
  );

  const errorMessage = isFoodPage ? foodPaymentError : error;

  if (errorMessage) {
    return (
      <PaymentError
        message={paymentErrorText(errorMessage)}
        supportEmail={supportEmail}
      />
    );
  }

  if (isLoading || isFoodPaymentLoading) return <FullPageLoader />;

  return paymentData ? (
    <PaymentStatusCard
      status={paymentData?.transaction_status || paymentData?.status}
      messages={TransactionStatusMessage}
      actionButton={
        <Stack justifyContent="center" my="1rem" spacing="0.75rem">
          {whatsappNumber && (
            <WhatsAppSentNote
              kind={whatsappKind}
              number={whatsappNumber}
              {...whatsappNoteProps}
            />
          )}
          <Box
            px="1rem"
            py="0.75rem"
            borderLeft="3px solid"
            borderRadius="sm"
            fontSize="sm"
            {...completionMessageBoxProps}
          >
            <Flex align="flex-start" gap="0.5rem">
              <Icon as={FiInfo} boxSize="1rem" mt="0.15rem" flexShrink={0} />
              <Text fontStyle="italic" lineHeight="1.6">
                {completionMessage ?? DEFAULT_COMPLETION_MESSAGE}
              </Text>
            </Flex>
          </Box>
          <Button
            isLoading={isGeneratingTicket || isGeneratingFoodTicket}
            isDisabled={isGeneratingTicket || isGeneratingFoodTicket}
            variant="ghost"
            colorScheme="primary"
            {...downloadButtonProps}
            onClick={(e) => {
              e.stopPropagation();
              const response = isFoodPage
                ? generateFoodTicket({
                    idx: paymentData.idx,
                    cinema_hall,
                  })
                : generateTicket({
                    transaction_code: paymentData?.transaction_code,
                  });
              response
                .unwrap()
                .then((res: any) => {
                  if ("data" in res && res.data instanceof Blob)
                    downloadFile(res.data, `ticket.pdf`);
                  success("Ticket Downloaded Successfully");
                })
                .catch((err) => {
                  catchErrorHandler(err, errorToast);
                });
            }}
          >
            Download Tickets
          </Button>
        </Stack>
      }
      {...props}
    />
  ) : null;
}
